import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescribedField from '../DescribedField';

const emptyItem = () => ({
  id: crypto.randomUUID(),
  title: '',
  institution: '',
  yearStart: '',
  yearEnd: '',
  description: '',
});

/**
 * ExperienceBlockEditor — Editor do bloco Formação e Experiência (só professional)
 * TODO 4.11
 *
 * Props:
 *   - value: { items: [{ id, title, institution, yearStart, yearEnd, description }] }
 *   - onChange: (patch) => void
 *   - context: object
 */
const ExperienceBlockEditor = ({ value = {}, onChange }) => {
  const { items = [] } = value;

  const patchItems = (newItems) => onChange({ ...value, items: newItems });

  const addItem = () => patchItems([...items, emptyItem()]);

  const removeItem = (index) => patchItems(items.filter((_, i) => i !== index));

  const updateItem = (index, field, val) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: val } : item
    );
    patchItems(updated);
  };

  return (
    <DescribedField
      label="Formação e experiência"
      description="Linha do tempo com graduações, especializações, cursos, residências e cargos. Construído manualmente — preencha com as informações que deseja destacar para os visitantes. Deixe 'Ano fim' vazio se ainda em andamento."
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item, index) => (
          <Paper key={item.id ?? index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Item {index + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => removeItem(index)}
                aria-label="Remover item"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              label="Título"
              value={item.title}
              onChange={(e) => updateItem(index, 'title', e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
              helperText="Nome do curso, especialização ou cargo (ex.: 'Residência em Dermatologia')."
            />
            <TextField
              fullWidth
              label="Instituição"
              value={item.institution}
              onChange={(e) => updateItem(index, 'institution', e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
              helperText="Instituição (ex.: 'USP', 'Hospital das Clínicas')."
            />
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <TextField
                label="Ano início"
                value={item.yearStart}
                onChange={(e) => updateItem(index, 'yearStart', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 4 }}
                placeholder="2015"
              />
              <TextField
                label="Ano fim"
                value={item.yearEnd}
                onChange={(e) => updateItem(index, 'yearEnd', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 4 }}
                placeholder="2017 (vazio = em andamento)"
              />
            </Box>
            <TextField
              fullWidth
              label="Descrição (opcional)"
              value={item.description}
              onChange={(e) => updateItem(index, 'description', e.target.value)}
              size="small"
              multiline
              rows={2}
              helperText="Detalhes adicionais, se necessário."
            />
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addItem}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Adicionar item
        </Button>
      </Box>
    </DescribedField>
  );
};

ExperienceBlockEditor.propTypes = {
  value: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        institution: PropTypes.string,
        yearStart: PropTypes.string,
        yearEnd: PropTypes.string,
        description: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default ExperienceBlockEditor;
